import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { FormField, FormFieldType, FormSchema } from '@kfolks/types'
import { FormFieldType as FormFieldTypeEnum } from '@kfolks/types'
import { useFormSchemaValidation, type FormSchemaValidationView } from './useFormSchemaValidation'

/**
 * Default validation view used when the live-validation injector is not
 * mounted. Means "validation is not wired" — consumers see no errors,
 * which is the correct fallback for unit tests that don't care about
 * validation.
 */
const NO_VALIDATION: FormSchemaValidationView = {
	errorsByFieldId: {},
	topLevelErrors: [],
	hasErrors: false,
	isPending: false,
}

/**
 * Form-builder shared state provider.
 *
 * Owns the in-progress `FormSchema` for the active request type, plus
 * field-selection state, and exposes mutators consumed by the canvas
 * (task 7.3), the properties panel (task 7.4), the preview (task 7.5),
 * the summary-template input (task 7.7), and the History panel (task 9.x).
 *
 * Per design D8, state stays local to the builder page — no global store —
 * and each builder owns its own context. Undo/redo is a non-goal for the
 * first cut (per the proposal's Out-of-Scope list); when added, a simple
 * command stack inside this context is the path.
 *
 * The shape of `formSchema` here is the canonical persisted shape (matches
 * `FormSchema` from `@kfolks/types`), so save → `PUT /requests/types/:id`
 * passes the value through unchanged. Validation against
 * `validateFormSchema` is the responsibility of the consuming components
 * and the save action; the context only holds state.
 */

export interface FormBuilderState {
	/** The in-progress schema. Always a valid `FormSchema` shape. */
	formSchema: FormSchema
	/**
	 * In-progress `summaryTemplate` — the Mustache-style one-liner that
	 * renders against `formData` to produce the row summary on
	 * `/requests-center/my`, dashboard widgets, and the digest. `null` means
	 * "use the auto-generator fallback at runtime"; `''` (empty string) is
	 * normalised to `null` on save. Lives alongside `formSchema` because
	 * the summary-template input shares state with it (autocomplete
	 * suggestions are sourced from `formSchema.fields[].id`).
	 */
	summaryTemplate: string | null
	/**
	 * Currently-selected field id, or null when no field is selected. Drives
	 * the properties panel's contents. `null` is also the default after
	 * insert / remove (selection clears so the canvas doesn't end up
	 * pointing at a removed id).
	 */
	selectedFieldId: string | null

	/** Replace the entire schema — used on initial load and on Raw JSON edits that round-trip back into Visual. */
	setFormSchema: (next: FormSchema) => void
	/** Replace the in-progress summary template. Pass `null` (or `''` which is normalised) to clear it. */
	setSummaryTemplate: (next: string | null) => void

	/**
	 * Append a new field of the given type at `atIndex` (or at the end when
	 * `atIndex` is `undefined`). Returns the generated field id so callers
	 * can immediately select it. The new field has a unique id of the shape
	 * `<fieldType>_<n>` where `n` is the lowest unused suffix in the
	 * current schema.
	 */
	insertField: (fieldType: FormFieldType, atIndex?: number) => string

	/**
	 * Move the field with id `fromId` to the position currently occupied by
	 * the field with id `toId`. No-op if the two are equal or either id is
	 * missing.
	 */
	reorderField: (fromId: string, toId: string) => void

	/** Remove the field with the given id. Clears selection if it pointed at the removed field. */
	removeField: (id: string) => void

	/** Select / deselect a field. Pass `null` to clear. */
	selectField: (id: string | null) => void

	/** Replace a single field by id (used by the properties panel for label/help/required edits). */
	updateField: (id: string, patch: Partial<FormField>) => void

	/**
	 * Live validation view (debounced 250 ms) of the in-progress
	 * `formSchema`. Returned per-field-id and top-level so consumers can
	 * surface errors where they're useful — canvas card badges, properties
	 * panel inline messages, top-of-page banner. Don't replicate the
	 * `validateFormSchema` call elsewhere; read from here.
	 */
	validation: FormSchemaValidationView
	/**
	 * Internal setter wired by `<LiveValidationInjector />` (mounted by
	 * `FormBuilderPage`). Underscore-prefixed to discourage external
	 * callers from writing validation state directly. Accepts the
	 * functional-updater form so the injector can guard on a structural
	 * compare and avoid re-render loops when the validator returns a new
	 * object reference with identical contents.
	 */
	__setValidation: (
		next: FormSchemaValidationView | ((prev: FormSchemaValidationView) => FormSchemaValidationView),
	) => void
}

const FormBuilderStateContext = createContext<FormBuilderState | null>(null)

const EMPTY_SCHEMA: FormSchema = { version: '1.0', fields: [] }

export function FormBuilderStateProvider({
	initialSchema,
	initialSummaryTemplate,
	children,
}: {
	initialSchema?: FormSchema
	initialSummaryTemplate?: string | null
	children: ReactNode
}) {
	const [formSchema, setFormSchema] = useState<FormSchema>(initialSchema ?? EMPTY_SCHEMA)
	const [summaryTemplate, setSummaryTemplateState] = useState<string | null>(initialSummaryTemplate ?? null)
	const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
	// Validation is written by an inner `<LiveValidationInjector />` (mounted
	// by `FormBuilderPage`); when not mounted, consumers see `NO_VALIDATION`.
	// This pattern lets the state provider stay free of `useQuery` so it can
	// be unit-tested without a `QueryClientProvider` ancestor.
	const [validation, setValidation] = useState<FormSchemaValidationView>(NO_VALIDATION)
	const setSummaryTemplate = useCallback((next: string | null) => {
		setSummaryTemplateState(typeof next === 'string' && next.length === 0 ? null : next)
	}, [])

	const insertField = useCallback((fieldType: FormFieldType, atIndex?: number): string => {
		let inserted = ''
		setFormSchema((prev) => {
			const id = generateUniqueFieldId(fieldType, prev.fields)
			const newField: FormField = {
				id,
				label: defaultLabelForType(fieldType),
				type: fieldType,
			}
			const idx = atIndex === undefined || atIndex < 0 || atIndex > prev.fields.length ? prev.fields.length : atIndex
			const fields = [...prev.fields.slice(0, idx), newField, ...prev.fields.slice(idx)]
			inserted = id
			return { ...prev, fields }
		})
		return inserted
	}, [])

	const reorderField = useCallback((fromId: string, toId: string) => {
		if (fromId === toId) return
		setFormSchema((prev) => {
			const fromIndex = prev.fields.findIndex((f) => f.id === fromId)
			const toIndex = prev.fields.findIndex((f) => f.id === toId)
			if (fromIndex === -1 || toIndex === -1) return prev
			const next = [...prev.fields]
			const [moved] = next.splice(fromIndex, 1)
			if (!moved) return prev
			next.splice(toIndex, 0, moved)
			return { ...prev, fields: next }
		})
	}, [])

	const removeField = useCallback((id: string) => {
		setFormSchema((prev) => ({ ...prev, fields: prev.fields.filter((f) => f.id !== id) }))
		setSelectedFieldId((prev) => (prev === id ? null : prev))
	}, [])

	const selectField = useCallback((id: string | null) => {
		setSelectedFieldId(id)
	}, [])

	const updateField = useCallback((id: string, patch: Partial<FormField>) => {
		setFormSchema((prev) => ({
			...prev,
			fields: prev.fields.map((f) => (f.id === id ? { ...f, ...patch, id: f.id, type: f.type } : f)),
		}))
	}, [])

	const value = useMemo<FormBuilderState>(
		() => ({
			formSchema,
			summaryTemplate,
			selectedFieldId,
			setFormSchema,
			setSummaryTemplate,
			insertField,
			reorderField,
			removeField,
			selectField,
			updateField,
			validation,
			__setValidation: setValidation,
		}),
		[
			formSchema,
			summaryTemplate,
			selectedFieldId,
			setSummaryTemplate,
			insertField,
			reorderField,
			removeField,
			selectField,
			updateField,
			validation,
		],
	)

	return <FormBuilderStateContext.Provider value={value}>{children}</FormBuilderStateContext.Provider>
}

export function useFormBuilderState(): FormBuilderState {
	const ctx = useContext(FormBuilderStateContext)
	if (!ctx) throw new Error('useFormBuilderState must be used within a FormBuilderStateProvider')
	return ctx
}

/**
 * Mounts `useFormSchemaValidation` against the current `formSchema` and
 * writes the result into the shared state. `FormBuilderPage` mounts this
 * once inside the provider so all consumers (canvas, properties panel,
 * eventual save button) read the same validation snapshot.
 *
 * Lives as a separate component so the state provider stays free of
 * `useQuery` (and its `QueryClientProvider` requirement). Tests that
 * don't need validation simply omit this injector.
 */
export function LiveValidationInjector() {
	const { formSchema, __setValidation } = useFormBuilderState()
	const view = useFormSchemaValidation(formSchema)
	// `useFormSchemaValidation` returns a fresh object literal on every render
	// (the early-return paths construct `{ ...EMPTY_VIEW, isPending: true }`,
	// and even the memoised path produces a new container). Pushing it
	// straight into context creates a render loop: context update → injector
	// re-render → new `view` object → useEffect fires → context update.
	//
	// Guard with a structural compare so we only push when the view's
	// contents actually changed.
	useEffect(() => {
		__setValidation((prev) => (validationViewsEqual(prev, view) ? prev : view))
	}, [view, __setValidation])
	return null
}

function validationViewsEqual(a: FormSchemaValidationView, b: FormSchemaValidationView): boolean {
	if (a === b) return true
	if (a.hasErrors !== b.hasErrors || a.isPending !== b.isPending) return false
	if (a.topLevelErrors.length !== b.topLevelErrors.length) return false
	for (let i = 0; i < a.topLevelErrors.length; i++) {
		if (a.topLevelErrors[i] !== b.topLevelErrors[i]) return false
	}
	const aIds = Object.keys(a.errorsByFieldId)
	const bIds = Object.keys(b.errorsByFieldId)
	if (aIds.length !== bIds.length) return false
	for (const id of aIds) {
		const aMsgs = a.errorsByFieldId[id] ?? []
		const bMsgs = b.errorsByFieldId[id] ?? []
		if (aMsgs.length !== bMsgs.length) return false
		for (let i = 0; i < aMsgs.length; i++) {
			if (aMsgs[i] !== bMsgs[i]) return false
		}
	}
	return true
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Generate a unique id of the shape `<fieldType>_<n>` where `n` is the
 * lowest unused integer (starting at 1) such that the resulting id does
 * not collide with any existing field id in the schema. This keeps ids
 * stable + predictable when admins drag two fields of the same type — they
 * get `text_1`, `text_2` rather than uuids the admin must rename to use
 * meaningfully in `summary_template` or conditional visibility rules.
 *
 * Admins are free to rename via the properties panel; this is just a
 * default that minimises the friction for someone who hasn't typed a name
 * yet.
 */
function generateUniqueFieldId(fieldType: FormFieldType, existing: ReadonlyArray<FormField>): string {
	const taken = new Set(existing.map((f) => f.id))
	let n = 1
	while (taken.has(`${fieldType}_${n}`)) n++
	return `${fieldType}_${n}`
}

/** Sensible default label per field type. Admin will overwrite via the properties panel. */
function defaultLabelForType(fieldType: FormFieldType): string {
	switch (fieldType) {
		case FormFieldTypeEnum.TEXT:
			return 'Text'
		case FormFieldTypeEnum.TEXTAREA:
			return 'Long text'
		case FormFieldTypeEnum.NUMBER:
			return 'Number'
		case FormFieldTypeEnum.EMAIL:
			return 'Email'
		case FormFieldTypeEnum.DATE:
			return 'Date'
		case FormFieldTypeEnum.SELECT:
			return 'Select'
		case FormFieldTypeEnum.MULTISELECT:
			return 'Multi-select'
		case FormFieldTypeEnum.AUTOCOMPLETE:
			return 'Autocomplete'
		case FormFieldTypeEnum.AUTOCOMPLETE_MULTI:
			return 'Autocomplete (multi)'
		case FormFieldTypeEnum.CHECKBOX:
			return 'Checkbox'
		case FormFieldTypeEnum.RADIO:
			return 'Radio'
		case FormFieldTypeEnum.USER_PICKER:
			return 'User picker'
		case FormFieldTypeEnum.USER_PICKER_MULTI:
			return 'User picker (multi)'
		case FormFieldTypeEnum.PROJECT_PICKER:
			return 'Project picker'
		case FormFieldTypeEnum.COUNTRY_PICKER:
			return 'Country picker'
		case FormFieldTypeEnum.FILE:
			return 'File upload'
		case FormFieldTypeEnum.FILE_MULTI:
			return 'File upload (multi)'
		case FormFieldTypeEnum.PRIORITY:
			return 'Priority'
		case FormFieldTypeEnum.PROVIDER_ACCESS_LIST:
			return 'Provider access list'
		default: {
			// Compile-time exhaustiveness — adding a new FormFieldType without a
			// label here is a build error.
			const _exhaustive: never = fieldType
			void _exhaustive
			return 'Field'
		}
	}
}
